import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "@/lib/api";
import { CategoryIcon, Empty, ICONS, NativeSelect, Spinner } from "@/components/Brand";
import { Confirm } from "@/components/Confirm";
import { ImageUpload } from "@/components/ImageUpload";
import { GoldBtn, PageTitle } from "@/pages/admin/AdminLayout";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

const BLANK = { name: "", description: "", icon: "layers", image: null, order: 0, active: true };

export default function CategoriesAdmin() {
  const [rows, setRows] = useState(null);
  const [f, setF] = useState(null);
  const load = useCallback(() => {
    api.get("/categories", { params: { all: true } }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const save = async (e) => {
    e.preventDefault();
    const body = { name: f.name, description: f.description, icon: f.icon, image: f.image, order: Number(f.order) || 0, active: f.active };
    try {
      if (f.id) await api.put(`/admin/categories/${f.id}`, body);
      else await api.post("/admin/categories", body);
      toast.success("Kategori disimpan");
      setF(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const del = async (c) => {
    try {
      await api.delete(`/admin/categories/${c.id}`);
      toast.success("Kategori dihapus");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <PageTitle title="Manajemen Kategori" desc="Kategori aktif otomatis tampil di halaman peserta.">
        <GoldBtn onClick={() => setF(BLANK)} data-testid="category-add-btn"><Plus className="h-4 w-4" />Tambah Kategori</GoldBtn>
      </PageTitle>
      {!rows ? <Spinner /> : rows.length === 0 ? <Empty text="Belum ada kategori." /> : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {rows.map((c) => (
            <div key={c.id} className="card-premium p-5" data-testid={`admin-category-${c.slug}`}>
              <div className="flex items-start gap-3">
                {c.image ? <img src={fileUrl(c.image)} alt="" className="h-12 w-12 rounded-lg object-cover" /> : <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[#0A192F] text-amber-400"><CategoryIcon name={c.icon} /></div>}
                <div className="min-w-0 flex-1">
                  <h3 className="font-bold text-[#0A192F]">{c.name}</h3>
                  <p className="line-clamp-2 text-xs text-slate-500">{c.description}</p>
                </div>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${c.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>{c.active ? "AKTIF" : "NONAKTIF"}</span>
              </div>
              <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
                <span>Urutan {c.order} · {c.subject_count} materi · {c.package_count} paket</span>
                <span className="flex gap-3">
                  <button onClick={() => setF(c)} data-testid={`category-edit-${c.slug}`}><Pencil className="h-4 w-4 text-slate-600" /></button>
                  <Confirm testid={`category-delete-${c.slug}`} title="Hapus kategori?" description="Semua materi dan soal dalam kategori ini akan ikut terhapus. Kategori yang masih memiliki paket tidak dapat dihapus." onConfirm={() => del(c)}
                    trigger={<button data-testid={`category-delete-btn-${c.slug}`}><Trash2 className="h-4 w-4 text-red-600" /></button>} />
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!f} onOpenChange={(o) => !o && setF(null)}>
        <DialogContent data-testid="category-dialog">
          <DialogHeader><DialogTitle>{f?.id ? "Edit kategori" : "Tambah kategori"}</DialogTitle></DialogHeader>
          {f && (
            <form onSubmit={save} className="space-y-3">
              <div><Label>Nama kategori</Label><Input required minLength={2} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="category-name-input" /></div>
              <div><Label>Deskripsi</Label><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="category-desc-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Ikon</Label><NativeSelect value={f.icon} onChange={(e) => setF({ ...f, icon: e.target.value })} data-testid="category-icon-select">{Object.keys(ICONS).map((k) => <option key={k} value={k}>{k}</option>)}</NativeSelect></div>
                <div><Label>Urutan</Label><Input type="number" value={f.order} onChange={(e) => setF({ ...f, order: e.target.value })} data-testid="category-order-input" /></div>
              </div>
              <div><Label>Gambar kategori (opsional, menggantikan ikon)</Label><ImageUpload value={f.image} onChange={(v) => setF({ ...f, image: v })} testid="category-image" /></div>
              <div className="flex items-center gap-3"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="category-active-switch" /><Label>Aktif (tampil untuk peserta)</Label></div>
              <GoldBtn type="submit" className="w-full justify-center py-2.5" data-testid="category-save-btn">Simpan</GoldBtn>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
